// src/screens/MartProducts.jsx
import {
  ArcElement,
  Chart as ChartJS,
  Legend,
  Tooltip
} from 'chart.js';
import { useEffect, useMemo, useState } from 'react';
import { Doughnut } from 'react-chartjs-2';
import '../css/MartProducts.css';
import useIdleLogout from "../hooks/useIdleLogout";

ChartJS.register(ArcElement, Tooltip, Legend);

export default function MartProducts() {
  useIdleLogout();
  const [products, setProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [filterCategory, setFilterCategory] = useState('All');

  // seed demo data – swap for API later
  useEffect(() => {
    setProducts([
      { id: 1, image: '/assets/img/product.png', name: 'Organic Apples 1kg', category: 'Grocery', brand: 'FreshFarm', sku: 'APL-1KG', price: 160, stock: 120, rating: 4.6, ordersToday: 42, status: 'Active' },
      { id: 2, image: '/assets/img/product.png', name: 'Whole Milk 1L', category: 'Dairy', brand: 'HappyCow', sku: 'MLK-1L', price: 75, stock: 0, rating: 4.1, ordersToday: 18, status: 'Out of Stock' },
      { id: 3, image: '/assets/img/product.png', name: 'Brown Bread', category: 'Bakery', brand: 'BakersBt', sku: 'BRD-BWN', price: 45, stock: 36, rating: 3.9, ordersToday: 27, status: 'Active' },
      { id: 4, image: '/assets/img/product.png', name: 'Basmati Rice 5kg', category: 'Grocery', brand: 'RoyalGrain', sku: 'RCE-5KG', price: 250, stock: 12, rating: 4.8, ordersToday: 12, status: 'Active' },
      { id: 5, image: '/assets/img/product.png', name: 'Laundry Detergent', category: 'Household', brand: 'CleanPro', sku: 'DTG-2KG', price: 135, stock: 6, rating: 4.3, ordersToday: 6, status: 'Active' },
      { id: 6, image: '/assets/img/product.png', name: 'Chocolate Cookies', category: 'Snacks', brand: 'CookieCo', sku: 'CKE-200', price: 60, stock: 0, rating: 4.0, ordersToday: 0, status: 'Archived' },
    ]);
  }, []);

  // metrics
  const total = products.length;
  const activeCount = products.filter(p => p.status === 'Active').length;
  const oosCount = products.filter(p => p.status === 'Out of Stock').length;
  const archivedCount = products.filter(p => p.status === 'Archived').length;

  // filters + search
  const filteredList = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return products.filter(p =>
      (filterStatus === 'All' || p.status === filterStatus) &&
      (filterCategory === 'All' || p.category === filterCategory) &&
      (
        term === '' ||
        p.name.toLowerCase().includes(term) ||
        p.category.toLowerCase().includes(term) ||
        p.brand.toLowerCase().includes(term) ||
        p.sku.toLowerCase().includes(term)
      )
    );
  }, [products, searchTerm, filterStatus, filterCategory]);

  // charts
  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: true,
    layout: { padding: { bottom: 40 } },
    plugins: {
      legend: { position: 'bottom', labels: { boxWidth: 12, padding: 8, font: { size: 11 } } }
    }
  };

  const statusData = {
    labels: ['Active', 'Out of Stock', 'Archived'],
    datasets: [{
      data: [activeCount, oosCount, archivedCount],
      backgroundColor: ['#198754', '#dc3545', '#6c757d']
    }]
  };

  const categories = useMemo(
    () => Array.from(new Set(products.map(p => p.category))),
    [products]
  );

  const categoryData = {
    labels: categories,
    datasets: [{
      data: categories.map(c => products.filter(p => p.category === c).length),
      backgroundColor: ['#0d6efd', '#6f42c1', '#dc3545', '#20c997', '#fd7e14', '#0dcaf0', '#6610f2', '#6c757d']
        .slice(0, categories.length)
    }]
  };

  return (
    <>
      {/* Metrics */}
      <div className="row row-cols-1 row-cols-md-2 row-cols-lg-4 g-3 mb-4 products-metrics">
        {[
          { label: 'Total Products', value: total },
          { label: 'Active', value: activeCount },
          { label: 'Out of Stock', value: oosCount },
          { label: 'Archived', value: archivedCount }
        ].map((m, i) => (
          <div className="col" key={i}>
            <div className="card metrics-card h-100">
              <div className="card-body">
                <h6 className="text-secondary mb-1">{m.label}</h6>
                <div className="fs-2 fw-bold">{m.value}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="row g-3 mb-4">
        <div className="col-md-6">
          <div className="card chart-card h-100">
            <div className="card-header">Product Status Distribution</div>
            <div className="card-body">
              <Doughnut data={statusData} options={doughnutOptions} />
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <div className="card chart-card h-100">
            <div className="card-header">Category Breakdown</div>
            <div className="card-body">
              <Doughnut data={categoryData} options={doughnutOptions} />
            </div>
          </div>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="card mb-3 search-filter-card">
        <div className="card-body searchbar-row-inline">
          <span className="search-icon"><i className="bi bi-search" /></span>

          <div className="input-group search-pill flex-grow-1">
            <input
              type="text"
              className="form-control"
              placeholder="Search by name, brand, SKU, category…"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="ghost-select">
            <select
              className="form-select select-ghost"
              value={filterCategory}
              onChange={e => setFilterCategory(e.target.value)}
            >
              <option value="All">All Categories</option>
              {categories.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          <div className="ghost-select">
            <select
              className="form-select select-ghost"
              value={filterStatus}
              onChange={e => setFilterStatus(e.target.value)}
            >
              <option value="All">All Statuses</option>
              <option value="Active">Active</option>
              <option value="Out of Stock">Out of Stock</option>
              <option value="Archived">Archived</option>
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="products-table-card">
        <div className="table-responsive">
          <table className="table table-hover align-middle table-pro">
            <colgroup>
              <col style={{ width: '70px' }} />
              <col style={{ width: '64px' }} />
              <col style={{ width: '180px' }} />
              <col style={{ width: '12%' }} />
              <col style={{ width: '12%' }} />
              <col style={{ width: '12%' }} />
              <col style={{ width: '10%' }} />
              <col style={{ width: '10%' }} />
              <col style={{ width: '110px' }} />
              <col style={{ width: '180px' }} />
            </colgroup>

            <thead>
              <tr>
                <th>Sl.No</th>
                <th>Image</th>
                <th>Name</th>
                <th>Category</th>
                <th>Brand</th>
                <th>SKU</th>
                <th>Price</th>
                <th>Stock</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {filteredList.map((p, i) => (
                <tr key={p.id}>
                  <td>{i + 1}</td>
                  <td>
                    <img
                      src={p.image}
                      alt=""
                      className="rounded object-cover"
                      width="40"
                      height="40"
                    />
                  </td>
                  <td className="name-cell">{p.name}</td>
                  <td>{p.category}</td>
                  <td>{p.brand}</td>
                  <td className="sku-cell">{p.sku}</td>
                  <td className="price-cell">Nu. {p.price.toFixed(2)}</td>
                  <td className={`stock-cell ${p.stock === 0 ? 'text-danger' : ''}`}>{p.stock}</td>
                  <td>
                    <span className={`badge status-${p.status.replaceAll(' ', '-').toLowerCase()}`}>
                      {p.status}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button className="btn-icon view" aria-label="View"><i className="bi bi-eye" /></button>
                      <button className="btn-icon edit" aria-label="Edit"><i className="bi bi-pencil-square" /></button>
                      <button className="btn-icon delete" aria-label="Delete"><i className="bi bi-trash" /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredList.length === 0 && (
                <tr>
                  <td colSpan="10" className="text-center py-3 text-muted">
                    No products match your filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <footer className="dashboard-footer mt-auto">
        <small>© {new Date().getFullYear()} SuperApp Admin • All rights reserved</small>
      </footer>
    </>
  );
}
